import React, { useMemo, useRef, useState, useEffect } from 'react';
import Layout from '../components/Layout';
import '../styles/CustomersList.css';
import '../styles/Pages.css';
import '../styles/InsightsPage.css';
import '../styles/OnboardingTasks.css';

type TabKey = 'tasks' | 'comments';

type TaskStatus = 'active' | 'in-progress' | 'done' | 'blocked';
const STATUS_OPTIONS: { value: TaskStatus; label: string; color: string }[] = [
  { value: 'active',      label: 'Active',      color: '#3b82f6' },
  { value: 'in-progress', label: 'In Progress', color: '#f59e0b' },
  { value: 'done',        label: 'Done',        color: '#10b981' },
  { value: 'blocked',     label: 'Blocked',     color: '#ef4444' },
];

interface OnboardingTask {
  id: string;
  title: string;
  client: string;
  project: string;
  tags: string[];
  status: TaskStatus;
  due: string | null;
  assignee: string | null;
  visibility: 'private' | 'public';
}

// Seed data mirrors the reference screenshot. Replace with a service when the
// Onboarding backend exists.
const SEED_TASKS: OnboardingTask[] = [
  ['Go-Live Readiness Checklist', 'Loyal Brokerage', 'Loyal Brokerage'],
  ["Clean Up UltraShip's Users Data", 'Loyal Brokerage', 'Loyal Brokerage'],
  ['Perform System Validation and Testing', 'Loyal Brokerage', 'Loyal Brokerage'],
  ['Configure System Settings', 'Loyal Brokerage', 'Loyal Brokerage'],
  ['Setup UltraShip Environment', 'Loyal Brokerage', 'Loyal Brokerage'],
  ['Go-Live Readiness Checklist', 'Max Freightlines LLC', 'Max Freightlines LLC'],
  ["Clean Up UltraShip's Users Data", 'Max Freightlines LLC', 'Max Freightlines LLC'],
  ['Configure System Settings', 'Max Freightlines LLC', 'Max Freightlines LLC'],
  ['Perform System Validation and Testing', 'Max Freightlines LLC', 'Max Freightlines LLC'],
  ['Setup UltraShip Environment', 'Max Freightlines LLC', 'Max Freightlines LLC'],
  ['Go-Live Readiness Checklist', 'ASL WORLDWIDE LLC', 'ASL WORLDWIDE LLC'],
  ["Clean Up UltraShip's Users Data", 'ASL WORLDWIDE LLC', 'ASL WORLDWIDE LLC'],
  ['Perform System Validation and Testing', 'ASL WORLDWIDE LLC', 'ASL WORLDWIDE LLC'],
  ['Configure System Settings', 'ASL WORLDWIDE LLC', 'ASL WORLDWIDE LLC'],
  ['Sign & Upload NDA', 'ASL WORLDWIDE LLC', 'ASL WORLDWIDE LLC'],
  ['Platform Overview & Solution Walkthrough', 'ASL WORLDWIDE LLC', 'ASL WORLDWIDE LLC'],
].map(([title, client, project], i) => ({
  id: `t-${i}`,
  title,
  client,
  project,
  tags: [],
  status: 'active' as TaskStatus,
  due: null,
  assignee: 'U',
  visibility: 'private' as const,
}));

const LockIcon: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const OnboardingTasks: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('tasks');
  const [search, setSearch] = useState('');
  const [tasks, setTasks] = useState<OnboardingTask[]>(SEED_TASKS);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [statusMenuFor, setStatusMenuFor] = useState<string | null>(null);
  const statusMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!statusMenuFor) return;
    const h = (e: MouseEvent) => { if (statusMenuRef.current && !statusMenuRef.current.contains(e.target as Node)) setStatusMenuFor(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [statusMenuFor]);

  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () => tasks.filter(t => !q || t.title.toLowerCase().includes(q) || t.client.toLowerCase().includes(q) || t.project.toLowerCase().includes(q)),
    [tasks, q],
  );

  const allSelected = filtered.length > 0 && filtered.every(t => selected.has(t.id));
  const toggleAll = () => setSelected(prev => {
    if (allSelected) return new Set();
    return new Set(filtered.map(t => t.id));
  });
  const toggleOne = (id: string) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const setStatus = (id: string, status: TaskStatus) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, status } : t));
    setStatusMenuFor(null);
  };

  return (
    <Layout>
      <div className="customers-list-container">
        {/* Header: tabs left, search + filters right */}
        <div className="customers-header-row">
          <div className="insights-tabs" role="tablist">
            <button role="tab" aria-selected={activeTab === 'tasks'} className={`insights-tab ${activeTab === 'tasks' ? 'active' : ''}`} onClick={() => setActiveTab('tasks')}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={activeTab === 'tasks' ? '#4338ca' : '#6b7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
              <span>Tasks</span>
              <span className="insights-tab-count">{tasks.length}</span>
            </button>
            <button role="tab" aria-selected={activeTab === 'comments'} className={`insights-tab ${activeTab === 'comments' ? 'active' : ''}`} onClick={() => setActiveTab('comments')}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={activeTab === 'comments' ? '#4338ca' : '#6b7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
              <span>Comments</span>
            </button>
          </div>

          {activeTab === 'tasks' && (
            <div className="header-right-section">
              <button className="onb-task-filters">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                Task Filters
              </button>
              <div className="search-bar">
                <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                <input type="text" placeholder="Search" value={search} onChange={e => setSearch(e.target.value)} className="search-input" />
              </div>
            </div>
          )}
        </div>

        {activeTab === 'tasks' ? (
          <div className="onb-task-wrap">
            <label className="onb-select-all">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} />
              <span>Select All</span>
            </label>

            <div className="onb-table">
              <div className="onb-row onb-head">
                <span className="onb-col-check" />
                <span className="onb-col-title">Title</span>
                <span className="onb-col-client">Client</span>
                <span className="onb-col-project">Project</span>
                <span className="onb-col-tags">Tags</span>
                <span className="onb-col-status">Status</span>
                <span className="onb-col-due">Due/Done</span>
                <span className="onb-col-assignee">Assignee</span>
                <span className="onb-col-vis">Visibility</span>
                <span className="onb-col-more" />
              </div>

              {filtered.map(t => {
                const cfg = STATUS_OPTIONS.find(s => s.value === t.status)!;
                return (
                  <div key={t.id} className="onb-row">
                    <span className="onb-col-check">
                      <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleOne(t.id)} />
                    </span>
                    <span className="onb-col-title onb-title-text">{t.title}</span>
                    <span className="onb-col-client onb-link" title={t.client}>{t.client}</span>
                    <span className="onb-col-project onb-link" title={t.project}>{t.project}</span>
                    <span className="onb-col-tags" />
                    <span className="onb-col-status">
                      <div className="onb-status-wrap" ref={statusMenuFor === t.id ? statusMenuRef : undefined}>
                        <button type="button" className="onb-status-pill" style={{ background: `${cfg.color}1a`, color: cfg.color }} onClick={() => setStatusMenuFor(p => p === t.id ? null : t.id)}>
                          <span className="onb-status-dot" style={{ borderColor: cfg.color }} />
                          {cfg.label}
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9" /></svg>
                        </button>
                        {statusMenuFor === t.id && (
                          <div className="onb-status-menu">
                            {STATUS_OPTIONS.map(o => (
                              <button key={o.value} type="button" className={`onb-status-option ${o.value === t.status ? 'selected' : ''}`} onClick={() => setStatus(t.id, o.value)}>
                                <span className="onb-status-dot" style={{ borderColor: o.color }} />
                                {o.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </span>
                    <span className="onb-col-due">{t.due || ''}</span>
                    <span className="onb-col-assignee">
                      <span className="onb-avatar">{(t.assignee || 'U').charAt(0).toUpperCase()}</span>
                    </span>
                    <span className="onb-col-vis"><LockIcon /></span>
                    <span className="onb-col-more">
                      <button type="button" className="onb-more-btn" title="More">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>
                      </button>
                    </span>
                  </div>
                );
              })}

              {filtered.length === 0 && (
                <div className="onb-empty">No tasks found.</div>
              )}
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">💬</div>
            <h2>Comments</h2>
            <p>Discuss onboarding progress here. This section is coming soon.</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OnboardingTasks;
